import { Capacitor } from "@capacitor/core";
import { useEffect, useRef, useState } from "react";
import {
  INSTALL_DISMISS_KEY,
  installSurface,
  isIosInstallTarget,
  isStandaloneDisplay,
  type BeforeInstallPromptLike,
  type InstallSnapshot,
  type InstallSurface,
} from "./pwaInstall";

const HIDDEN: InstallSnapshot = {
  standalone: false,
  dismissed: false,
  canPrompt: false,
  ios: false,
};

export function useInstallHint(): {
  mode: InstallSurface;
  onInstall: () => void;
  onDismiss: () => void;
} {
  const [snapshot, setSnapshot] = useState<InstallSnapshot>(HIDDEN);
  const deferred = useRef<BeforeInstallPromptLike | null>(null);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setSnapshot((current) => ({ ...current, standalone: true, canPrompt: false }));
      return;
    }
    const standalone = isStandaloneDisplay({
      navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone,
      displayModeStandalone: window.matchMedia("(display-mode: standalone)").matches,
    });
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(INSTALL_DISMISS_KEY) === "1";
    } catch {
      dismissed = false;
    }
    const ios = isIosInstallTarget(navigator.userAgent, navigator.maxTouchPoints ?? 0);
    setSnapshot((current) => ({ ...current, standalone, dismissed, ios }));

    const onPrompt = (event: Event) => {
      event.preventDefault();
      deferred.current = event as BeforeInstallPromptLike;
      setSnapshot((current) => ({ ...current, canPrompt: true }));
    };
    const onInstalled = () => {
      deferred.current = null;
      setSnapshot((current) => ({ ...current, standalone: true, canPrompt: false }));
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onInstall = () => {
    const event = deferred.current;
    if (!event) return;
    void event
      .prompt()
      .then(() => event.userChoice)
      .then((choice) => {
        deferred.current = null;
        if (choice.outcome === "accepted") {
          setSnapshot((current) => ({ ...current, canPrompt: false, standalone: true }));
        }
      })
      .catch(() => undefined);
  };

  const onDismiss = () => {
    try {
      window.sessionStorage.setItem(INSTALL_DISMISS_KEY, "1");
    } catch {
      /* Private mode can block storage. Hide the banner for this view anyway. */
    }
    setSnapshot((current) => ({ ...current, dismissed: true }));
  };

  return { mode: installSurface(snapshot), onInstall, onDismiss };
}
